import React, { useMemo, useState } from 'react';
import Icon from '../../../Components/Icons/Icon';
import { statuses } from '../../../Data/Jobs';
import { classNames, dateToString, initialsFromString, ltrim, weeksInYear, weekString } from '../../../Services/helpers';
import { useRounds } from '../../../Data/Rounds';
import Loading, { PageLoading } from '../../../Components/Loading';
import RelativeDate from '../../../Components/Formatting/RelativeDate';
import UserName from '../../../Components/Formatting/UserName';
import { usePreference } from '../../../Data/Preference';
import AlertBadge from '../../../Components/Icons/AlertBadge';
import Schedule from '../Widgets/ScheduleOverview/Schedule';
import Tile from '../../../Components/Containers/Tile';
import Button from '../../../Components/Button/Button';
import { useDialogues } from '../../../Components/Dialogues';
import Duration from '../../../Components/Formatting/Duration';
import Time from '../../../Components/Formatting/Time';
import { useRedirect } from '../../../Services/helpers';
import { IDs } from '../../../Data/Misc';
import { Money } from '@/Components/Primitives';
import { useWeekOverview } from '../../../Data/WeekOverview';
import { executionModes } from '../../../Data/Journal';
import {
    buildScheduleModalPayload,
    buildWeekOverviewDays,
    DEFAULT_DAILY_TRACKER_GROUP_BY,
    DEFAULT_DAILY_TRACKER_ROW_END_METRIC,
    groupRowJobsLabel,
    sortedStatusesForGroup,
    sumJobsCostPence,
} from './weekOverviewHelper';
import DailyTrackerPreferences from './DailyTrackerPreferences';
import { NoValue } from '../../../Components/Primitives';

const DailyTracker = () => {
    const thisWeek = weekString();
    const [week, setWeek] = usePreference('dailyTracker:week', weekString(), true);
    const [groupBy] = usePreference('dailyTracker:groupBy', DEFAULT_DAILY_TRACKER_GROUP_BY);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const {rounds} = useRounds();
    const {overview, isLoading, refresh, executeTrigger} = useWeekOverview(week === thisWeek ? null : week, true);

    const roundsById = useMemo(
        () => Object.fromEntries((rounds ?? []).map(round => [round.id, round])),
        [rounds],
    );
    const days = useMemo(() => buildWeekOverviewDays(overview, groupBy), [overview, groupBy]);
    const today = dateToString(new Date());

    const prevWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w--;
        if (w < 1) {
            y--;
            w = 52;
        }
        setWeek(`${y}-W${w}`);
    }
    const nextWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w++;
        if (w > weeksInYear(y)) {
            y++;
            w = 1;
        }
        setWeek(`${y}-W${w}`);
    }

    return <Tile title="Daily tracker"
        icon="CircleCheck"
        options={<div className="flex flex-row items-center gap-2 text-muted text-sm">
            <Icon icon="Gear" size="0.5x" onClick={() => setPreferencesOpen(true)} />
            <Icon icon="LeftChevron" size="0.5x" onClick={prevWeek} />
            <DateNav {...{days, isLoading}} />
            <Icon icon="RightChevron" size="0.5x" onClick={nextWeek} />
        </div>}
        flush
    >
        {days ? <Inner {...{days, today, groupBy, roundsById, executeTrigger, onScheduled: refresh}} /> : <PageLoading />}
        <DailyTrackerPreferences isOpen={preferencesOpen} setIsOpen={setPreferencesOpen} />
    </Tile>
};

const DateNav = ({days, isLoading}) => {
    if (isLoading || !days) {
        return <Loading />;
    }

    const from = ltrim(days[0].date.split('-')[2], '0');
    const last = days[days.length - 1].date;
    const month = new Date(last).toLocaleString('en-GB', { month: 'short' });
    const to = ltrim(last.split('-')[2], '0')+' '+month;

    return <div className="flex flex-row items-center gap-2">
        {`${from} - ${to}`}
    </div>;
}

const Inner = ({days, today, groupBy, roundsById, executeTrigger, onScheduled}) => {
    return <div className="flex flex-col gap-2">
        {days.map(day => <div key={day.date} className="p-2 surface-1 rounded-lg surface-outline">
            <DateRow {...{day, today, groupBy, roundsById, executeTrigger, onScheduled}} />
        </div>)}
    </div>;
};

const DateRow = ({day, today, groupBy, roundsById, executeTrigger, onScheduled}) => {
    const [expanded, setExpanded] = useState(false);
    const {date, statusConfig, dateStatus, totalJobs, doneJobs, totalCost, triggers, groups} = day;

    const toggleExpanded = () => setExpanded(!expanded);

    return <div className="flex flex-row items-start justify-between gap-4">
        <div className="cursor-pointer shrink-0" onClick={toggleExpanded}>
            <AlertBadge
                icon={statusConfig.icon}
                size="large"
                theme={statusConfig.theme}
            />
        </div>
        <div className="flex flex-col gap-2 w-full min-w-0">
            <div
                className="flex flex-row items-center justify-between gap-2 w-full cursor-pointer"
                onClick={toggleExpanded}
            >
                <p className={classNames('font-bold grow', date < today && 'text-muted')}>
                    <RelativeDate date={date} />
                    <br />
                    <span className="text-muted text-sm font-normal">
                        {totalJobs > 0 && `${doneJobs} of ${totalJobs} jobs done`}
                        {totalJobs === 0 && 'No jobs scheduled'}
                    </span>
                </p>
                <div className="shrink-0 flex flex-col items-end">
                    <p className={classNames('text-sm font-bold', statusConfig.textColor)}>
                        {dateStatus}
                    </p>
                    <p className="text-sm text-muted">
                        {totalCost > 0 && <Money pence={totalCost} />}
                    </p>
                </div>
                <div className="shrink">
                    <Icon icon={expanded ? 'UpChevron' : 'DownChevron'} size="1x" className="ml-2" />
                </div>
            </div>
            <div className={classNames('flex flex-col gap-2 transition-all duration-300 h-auto overflow-hidden', expanded ? 'max-h-fit' : 'max-h-0')}>
                {Object.keys(groups).map(groupId =>
                    <GroupRow
                        key={groupId}
                        date={date}
                        groupId={groupId}
                        group={groups[groupId]}
                        groupBy={groupBy}
                        roundsById={roundsById}
                        onScheduled={onScheduled}
                    />
                )}
                <Triggers {...{date, triggers, executeTrigger}} />
            </div>
        </div>
    </div>;
}

const GroupRow = ({date, groupId, group, groupBy, roundsById, onScheduled}) => {
    const sortedStatuses = useMemo(() => sortedStatusesForGroup(group.jobsByStatus), [group.jobsByStatus]);
    const scheduledJobs = useMemo(
        () => Object.values(group.jobsByStatus).flat(),
        [group.jobsByStatus],
    );
    const [scheduling, setScheduling] = useState(false);
    const {go} = useRedirect();
    const {totals, suggestedJobs, isWorkerBlocked} = group;

    const showSuggestedJobs = suggestedJobs.length > 0 && sortedStatuses.length === 0;
    const schedulePayload = useMemo(
        () => buildScheduleModalPayload(suggestedJobs, groupBy, groupId, roundsById),
        [suggestedJobs, groupBy, groupId, roundsById],
    );
    const workQuery = groupBy === 'roundId' ? 'roundId' : 'assignedTo';
    const workFilter = groupId === 'unassigned' ? IDs.EMPTY : groupId;

    return <div className="flex flex-row items-center gap-2 w-full min-w-0"
        onClick={() => go(`/work/${date}?${workQuery}=${workFilter}`)}
    >
        <div className="w-10 shrink-0 flex flex-row items-center justify-center gap-0.5 text-muted text-sm">
            <GroupRowLabel {...{groupId, groupBy, roundsById}} />
            {isWorkerBlocked && <Icon icon="Ban" theme="solid" className="fill-red-500" size="0.5x" />}
        </div>
        <div className="grow flex flex-row items-center gap-2">
            <div className="grow">
                {showSuggestedJobs && <div
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setScheduling(true);
                    }}
                    className="text-xs bg-brand-secondary text-white px-2 py-1 rounded-full inline-block"
                >
                    {suggestedJobs.length} Suggested
                </div>}
                {(sortedStatuses.length > 0) && (
                    <div className="flex flex-row h-2 w-full rounded-full overflow-hidden">
                        {sortedStatuses.map(({status, totalDuration}, index) => {
                            const statusConfig = statuses[status];
                            const isFirst = index === 0;
                            const isLast = index === sortedStatuses.length - 1;

                            return (
                                <div
                                    key={status}
                                    className={classNames(
                                        'h-full min-w-0',
                                        statusConfig?.bgColor,
                                        isFirst && 'rounded-l-full',
                                        isLast && 'rounded-r-full',
                                    )}
                                    style={{ flex: totalDuration || 1 }}
                                />
                            );
                        })}
                    </div>
                )}
                {!showSuggestedJobs && sortedStatuses.length === 0 && (
                    <div className="h-2 w-2 bg-gray-200 rounded-full" />
                )}
            </div>
            {!showSuggestedJobs && <GroupRowEnd
                totals={totals}
                scheduledJobs={scheduledJobs}
                suggestedJobs={suggestedJobs}
                showSuggestedJobs={showSuggestedJobs}
            />}
            <div className="shrink-0 w-6" aria-hidden />
        </div>
        <Schedule
            isOpen={scheduling}
            setIsOpen={setScheduling}
            date={date}
            assignTo={schedulePayload.assignTo}
            rounds={schedulePayload.rounds}
            jobs={schedulePayload.jobs}
            onScheduled={onScheduled}
            onClose={() => setScheduling(false)}
        />
    </div>;
};

const GroupRowEnd = ({totals, scheduledJobs, suggestedJobs, showSuggestedJobs}) => {
    const [rowEndMetric] = usePreference('dailyTracker:rowEndMetric', DEFAULT_DAILY_TRACKER_ROW_END_METRIC);
    const jobs = showSuggestedJobs ? suggestedJobs : scheduledJobs;

    return <div className="w-14 shrink-0 text-right overflow-visible whitespace-nowrap text-xs text-muted tabular-nums">
        {rowEndMetric === 'cost' && <Money pence={sumJobsCostPence(jobs)} />}
        {rowEndMetric === 'duration' && (
            <Duration
                sum={jobs.map(job => job.duration ?? '00:15:00')}
                short
                hideSeconds
            />
        )}
        {rowEndMetric === 'jobs' && (
            showSuggestedJobs ?
                suggestedJobs.length :
                groupRowJobsLabel(totals)
        )}
    </div>;
};

const Triggers = ({date, triggers, executeTrigger}) => {
    const {confirm, info} = useDialogues();

    const handleExecute = (trigger) => {
        const available = trigger.availableCount ?? 0;

        if (available === 0) {
            info({
                theme: 'primary',
                title: 'No available notifications',
                message: `All notifications for "${trigger.type}" have already been sent today.`,
            });
            return;
        }

        const isScheduled = trigger.executionMode === executionModes.SCHEDULED && trigger.schedule;
        const message = isScheduled
            ? <>
                This trigger is scheduled to be sent shortly after <Time time={trigger.schedule} />, but you can send {available} notification{available === 1 ? '' : 's'} now to customers.
            </>
            : `This will send ${available} notification${available === 1 ? '' : 's'} to customers.`;

        confirm(
            {
                theme: 'primary',
                title: `Execute ${trigger.triggerType}?`,
                message,
                confirmLabel: 'Execute',
            },
            () => executeTrigger(trigger.id, date)
        );
    }

    if (!triggers || triggers.length === 0) {
        return null;
    }

    return <div className="flex flex-row items-center gap-2">
        {triggers.map(trigger => {
            return <Button key={trigger.id}
                onClick={() => handleExecute(trigger)}
                label={trigger.type}
                icon={trigger.availableCount > 0 ?
                    (trigger.executionMode === executionModes.SCHEDULED ? 'Clock' : 'PaperPlane') :
                    'Check'
                }
                theme="light"
                disabled={trigger.availableCount === 0}
            />
        })}
    </div>;
}

const GroupRowLabel = ({groupId, groupBy, roundsById}) => {
    if (groupId === 'unassigned') {
        return <NoValue />
    }

    if (groupBy === 'roundId') {
        return initialsFromString(roundsById[groupId]?.name);
    }

    return <UserName userId={groupId} you={false} initials />;
};

export default DailyTracker;
